import { useState, useEffect, useRef } from 'react';
import { Grid, Typography, InputBase } from '@mui/material';
import { ColorPicker } from '../../../../../Global/Components';
import useElementSize from '../../../../../Global/hooks/useElementSize';
import { v4 } from 'uuid';
import MenuList from '../MenuList';

export default function TextArea({ large, onEdit, initial, editable, onDelete, onChange }) {
  const [text, setText] = useState(initial?.text || '');
  const [colorPicker, setColorPicker] = useState(null);
  const [color, setColor] = useState(undefined);
  const [fontSettings, setFontSettings] = useState({});
  const [elementId] = useState(v4());
  const { height, width } = useElementSize(elementId);
  const textAreaRef = useRef(null);
  const containerRef = useRef(null);
  const fontSizeCoefficient = fontSettings?.size
    ? fontSettings.size === '24px'
      ? 1
      : fontSettings.size === '16px'
      ? 3.5
      : fontSettings.size === '10px'
      ? 8
      : 1
    : 1;
  const fontSize = height / fontSizeCoefficient - (fontSizeCoefficient === 1 ? 25 : 0);

  useEffect(() => {
    if (initial?.color) {
      setColor(initial.color);
    }
    if (initial?.fontSettings) {
      setFontSettings(initial.fontSettings);
    }
    //eslint-disable-next-line
  }, [initial]);

  const checkOverflow = () => {
    const container = containerRef.current;
    const textArea = textAreaRef.current;

    if (textArea && container) {
      const isOverflowing =
        textArea.scrollHeight > container.offsetHeight || textArea.scrollWidth > container.offsetWidth;
      return isOverflowing;
    }

    return false;
  };

  useEffect(() => {
    const resizeTextArea = () => {
      const textArea = textAreaRef.current;
      if (textArea) {
        textArea.style.height = 'auto';
        //eslint-disable-next-line
        textArea.style.height = textArea.scrollHeight + 'px';
      }

      resizeTextArea();
    };
  }, [text]);

  function saveColor(col) {
    setColor(col);
    onChange({ color: col });
  }

  function saveFontSettings(settings) {
    setFontSettings(settings);
    onChange({ fontSettings: settings });
  }

  return (
    <Grid
      container
      style={{ height: '100%', backgroundColor: color || 'inherit', padding: 2 }}
      id={elementId}
      ref={containerRef}
    >
      <Grid
        container
        item
        style={{ overflow: 'hidden', position: 'relative', flex: 1, padding: 2 }}
        alignItems="center"
      >
        {editable ? (
          <Grid
            container
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              left: 0,
              top: 0,
            }}
            wrap="nowrap"
          >
            <InputBase
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                onEdit({ text: e.target.value });
              }}
              onMouseDown={(e) => e.stopPropagation()}
              ref={textAreaRef}
              fullWidth
              multiline
              placeholder="Enter text"
              inputProps={{
                maxLength: large ? 256 : 2048,
                style: {
                  width: '100%',
                  minHeight: 'fit-content',
                  textAlign: fontSettings?.alignment ? fontSettings.alignment : 'left',
                  margin: 0,
                  lineHeight: 1,
                  fontSize,
                },
              }}
              style={{
                flexGrow: 1,
                overflow: 'auto',
                alignItems: fontSizeCoefficient === 1 ? 'flex-start' : 'center',
              }}
            />
            <ColorPicker
              anchor={colorPicker}
              open={Boolean(colorPicker)}
              column={400}
              customPicker
              onClose={(res) => {
                if (res?.color) {
                  saveColor(res?.color);
                }
                setColorPicker(null);
              }}
              className="non-draggable"
            />
            <MenuList
              mode={'header'}
              onDelete={onDelete}
              setColorPicker={setColorPicker}
              color={color}
              onChange={onChange}
              fontSettings={fontSettings}
              saveFontSettings={saveFontSettings}
              sx={{ position: 'absolute', top: 5, right: 5 }}
            />
          </Grid>
        ) : (
          <Grid
            container
            alignItems="flex-start"
            style={{
              height: '95%',
              position: 'absolute',
              left: 0,
              top: 0,
              alignItems: fontSizeCoefficient !== '24px' ? 'center' : 'flex-start',
            }}
          >
            <Typography
              style={{
                fontSize,
                width: '100%',
                padding: '10px',
                overflow: 'hidden',
                whiteSpace: 'break-spaces',
                marginLeft: 16,
                marginRight: 16,
                textAlign: fontSettings?.alignment || 'left',
                lineHeight: 1,
              }}
            >
              {initial?.text}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
